import type {
    ExpenseRecord,
    ExpenseCalculationInput,
    CategoryCostBreakdown,
    ExpenseCategoryEntry,
    PmPartPriceSnapshot,
    StoredLaborLine,
} from "../types/costTrackingTypes";
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface ExpenseCalendarParts {
    year: number;
    month: number;
    day: number;
}

/** Local calendar day for an expense timestamp. */
export function expenseCalendarParts(dateStr: string): ExpenseCalendarParts | null {
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) return null;
    return { year: parsed.getFullYear(), month: parsed.getMonth(), day: parsed.getDate() };
}

const INTERNAL_RECORD_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Invoice / PO / WO is a person's number. A stored report id is not shown. */
export function expenseInvoiceLabel(invoiceNumber?: string | null): string {
    const value = invoiceNumber?.trim() || '';
    if (!value || INTERNAL_RECORD_ID.test(value)) return '';
    return value;
}

/** Drop a trailing database id from a cost description. The equipment name stays in the Item column. */
export function expenseWorkDescriptionLabel(workDescription?: string | null): string {
    return (workDescription || '')
        .trim()
        .replace(/\s*·\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '')
        .trim();
}

/** "Oct 6 2026" for the expense list. */
export function formatExpenseDate(dateStr: string): string {
    const parts = expenseCalendarParts(dateStr);
    if (!parts) return dateStr;
    return `${SHORT_MONTHS[parts.month]} ${parts.day} ${parts.year}`;
}

/** "10/06/2026" for the expense detail. */
export function formatExpenseDateNumeric(dateStr: string): string {
    const parts = expenseCalendarParts(dateStr);
    if (!parts) return dateStr;
    const month = String(parts.month + 1).padStart(2, '0');
    const day = String(parts.day).padStart(2, '0');
    return `${month}/${day}/${parts.year}`;
}

/** YYYY-MM-DD for a date input, using the same calendar day as the list. */
export function expenseDateInputValue(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    const parts = expenseCalendarParts(dateStr);
    if (!parts) return dateStr.split('T')[0] || '';
    const month = String(parts.month + 1).padStart(2, '0');
    const day = String(parts.day).padStart(2, '0');
    return `${parts.year}-${month}-${day}`;
}

/**
 * Store a date-input day at local noon so the calendar day does not shift across US time zones.
 */
export function dateInputToIso(dateStr: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr.trim());
    if (!match) {
        const parsed = new Date(dateStr);
        return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
    }
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0).toISOString();
}

// ----- Total Expense Calculation (Req 4.13, 5.4) -----

/**
 * Calculates the total expense from PM expense, category costs, and labor.
 * totalExpense = pmExpense + sum(categoryCosts) + (laborHoursST × laborRateST) + (laborHoursOT × laborRateOT)
 *
 * Validates: Requirements 4.13, 5.4
 */
export function calculateTotalExpense(input: ExpenseCalculationInput): number {
    const laborCost =
        input.laborHoursST * input.laborRateST +
        input.laborHoursOT * input.laborRateOT;
    const categoryCost = input.categoryEntries.reduce(
        (sum, entry) => sum + entry.cost,
        0,
    );
    return input.pmExpense + categoryCost + laborCost;
}

// ----- ITD Cost Per UM (Req 10.3) -----

/**
 * Calculates Inception-to-Date cost per Unit of Measure.
 * Returns 0 if currentUMCounterValue is zero or negative.
 *
 * Validates: Requirements 10.3
 */
export function calculateITDCostPerUM(
    totalCost: number,
    currentUMCounterValue: number,
): number {
    if (currentUMCounterValue <= 0) return 0;
    return totalCost / currentUMCounterValue;
}

// ----- Labor Cost (Req 13.5) -----

/**
 * Calculates labor cost from straight-time and overtime hours and rates.
 * laborCost = (stHours × stRate) + (otHours × otRate)
 *
 * Validates: Requirements 13.5
 */
export function parseLaborEntries(raw: unknown): StoredLaborLine[] {
    let parsed: unknown = raw;
    if (typeof raw === 'string') {
        if (!raw.trim()) return [];
        try {
            parsed = JSON.parse(raw);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item) => {
        if (!item || typeof item !== 'object') return [];
        const row = item as Record<string, unknown>;
        const hoursST = Number(row.hoursST) || 0;
        const hoursOT = Number(row.hoursOT) || 0;
        if (hoursST <= 0 && hoursOT <= 0) return [];
        const rateST = Number(row.rateST) || 0;
        const rateOT = Number(row.rateOT) || 0;
        return [{
            categoryId: typeof row.categoryId === 'string' ? row.categoryId : '',
            categoryName: typeof row.categoryName === 'string' ? row.categoryName : '',
            hoursST,
            hoursOT,
            rateST,
            rateOT,
            cost: Number(row.cost) || hoursST * rateST + hoursOT * rateOT,
        }];
    });
}

/**
 * Categories for this equipment type, plus older categories that were not
 * assigned to an equipment type.
 */
export function expenseCategoriesForEquipment<T extends { equipmentCategoryId?: string | null }>(
    categories: T[],
    equipmentCategoryId: string,
): T[] {
    const equipmentId = equipmentCategoryId.trim();
    if (!equipmentId) return categories.filter((category) => !category.equipmentCategoryId);
    return categories.filter(
        (category) => !category.equipmentCategoryId || category.equipmentCategoryId === equipmentId,
    );
}

export function laborHoursLabel(hoursST: number, hoursOT: number): string {
    return [
        hoursST > 0 ? `${hoursST} ST hrs` : '',
        hoursOT > 0 ? `${hoursOT} OT hrs` : '',
    ].filter(Boolean).join(' + ');
}

export function calculateLaborCost(
    stHours: number,
    otHours: number,
    stRate: number,
    otRate: number,
): number {
    return stHours * stRate + otHours * otRate;
}


// ----- Cost Summary Date Calculations (Req 3.2, 3.3, 3.4) -----

/**
 * Sums totalExpense for all expense records with dateOfService in the current calendar year.
 *
 * Validates: Requirements 3.2
 */
export function calculateCostsSinceStartOfYear(
    expenses: ExpenseRecord[],
): number {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    return expenses
        .filter((e) => new Date(e.dateOfService) >= startOfYear)
        .reduce((sum, e) => sum + e.totalExpense, 0);
}

/**
 * Sums totalExpense for all expense records with dateOfService in the previous calendar month.
 *
 * Validates: Requirements 3.3
 */
export function calculateCostsInPreviousMonth(
    expenses: ExpenseRecord[],
): number {
    const now = new Date();
    const startOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    return expenses
        .filter((e) => {
            const d = new Date(e.dateOfService);
            return d >= startOfPrevMonth && d <= endOfPrevMonth;
        })
        .reduce((sum, e) => sum + e.totalExpense, 0);
}

/**
 * Sums totalExpense for all expense records with dateOfService in the current calendar month.
 *
 * Validates: Requirements 3.4
 */
export function calculateCostsInCurrentMonth(
    expenses: ExpenseRecord[],
): number {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return expenses
        .filter((e) => new Date(e.dateOfService) >= startOfMonth)
        .reduce((sum, e) => sum + e.totalExpense, 0);
}

// ----- Category Cost Breakdown (Req 10.3, 10.6) -----

/**
 * Aggregates costs by category for a set of expense records, including PM Services.
 * Returns an array of category names and their total costs.
 *
 * Validates: Requirements 10.3, 10.6
 */
export function getCostBreakdownForUnit(
    expenses: ExpenseRecord[],
): CategoryCostBreakdown[] {
    const map = new Map<string, number>();

    // Add PM expenses as a "PM Services" category
    const pmTotal = expenses.reduce(
        (sum, e) => sum + (e.pmExpense || 0),
        0,
    );
    if (pmTotal > 0) {
        map.set("PM Services", pmTotal);
    }

    // Add expense category costs
    for (const expense of expenses) {
        let categories: ExpenseCategoryEntry[] = [];
        try {
            const parsed = JSON.parse(expense.expenseCategories || "[]");
            if (Array.isArray(parsed)) categories = parsed;
        } catch {
            categories = [];
        }
        for (const cat of categories) {
            const cost = Number(cat?.cost);
            if (!cat?.categoryName || !Number.isFinite(cost)) continue;
            map.set(
                cat.categoryName,
                (map.get(cat.categoryName) || 0) + cost,
            );
        }
    }

    const laborTotal = expenses.reduce((sum, expense) => sum + laborNotAlreadyCategorized(expense), 0);
    if (laborTotal > 0) {
        map.set("Labor", (map.get("Labor") || 0) + laborTotal);
    }

    return Array.from(map.entries()).map(([categoryName, totalCost]) => ({
        categoryName,
        totalCost,
    }));
}

const LABOR_CATEGORY_NAMES = new Set(["labor", "internal labor"]);

function categoryLaborAlreadyListed(expense: Pick<ExpenseRecord, "expenseCategories">): number {
    let categories: ExpenseCategoryEntry[] = [];
    try {
        const parsed = JSON.parse(expense.expenseCategories || "[]");
        if (Array.isArray(parsed)) categories = parsed;
    } catch {
        return 0;
    }
    return categories.reduce((sum, cat) => {
        const name = cat.categoryName?.trim().toLowerCase() || "";
        if (!LABOR_CATEGORY_NAMES.has(name)) return sum;
        return sum + (Number(cat.cost) || 0);
    }, 0);
}

/** In-house labor dollars on one expense. Uses the stored total, then the labor lines. */
export function laborCostOfRecord(expense: Pick<ExpenseRecord, "totalLaborCost" | "laborEntries">): number {
    if ((expense.totalLaborCost ?? 0) > 0) return expense.totalLaborCost ?? 0;
    return parseLaborEntries(expense.laborEntries).reduce((sum, line) => {
        if (line.cost > 0) return sum + line.cost;
        return sum + line.hoursST * line.rateST + line.hoursOT * line.rateOT;
    }, 0);
}

/** Labor stored on the record that is not already a Labor or Internal Labor category line. */
function laborNotAlreadyCategorized(
    expense: Pick<ExpenseRecord, "totalLaborCost" | "laborEntries" | "expenseCategories">,
): number {
    return Math.max(0, laborCostOfRecord(expense) - categoryLaborAlreadyListed(expense));
}

/**
 * Assigned service-item lines go to their cost category.
 * Lines with no category stay in the caller’s fallback total (Daily Service or PM Services).
 */
export function groupPartSnapshotsByCostCategory(
    snapshots: PmPartPriceSnapshot[],
): { unassignedTotal: number; entries: ExpenseCategoryEntry[] } {
    const grouped = new Map<string, ExpenseCategoryEntry>();
    let unassignedTotal = 0;

    for (const line of snapshots) {
        const categoryName = line.expenseCategoryName?.trim() || '';
        if (!categoryName) {
            unassignedTotal += line.lineTotal;
            continue;
        }
        const categoryId = line.expenseCategoryId?.trim() || '';
        const key = categoryId || categoryName.toLowerCase();
        const existing = grouped.get(key);
        if (existing) {
            existing.cost += line.lineTotal;
        } else {
            grouped.set(key, { categoryId, categoryName, cost: line.lineTotal });
        }
    }

    return { unassignedTotal, entries: [...grouped.values()] };
}

/**
 * A service item may store only a cost category owned by the same company.
 * The stored name comes from that category, so a caller cannot pair another
 * company's id with a local name.
 */
export function costCategoryOwnedByCompany(
    companyId: string,
    category: { id?: string | null; companyId?: string | null; name?: string | null } | null | undefined,
): { expenseCategoryId: string; expenseCategoryName: string } {
    const expenseCategoryId = category?.id?.trim() || '';
    if (!companyId.trim() || !expenseCategoryId || category?.companyId !== companyId) {
        throw new Error('Cost category does not belong to this company');
    }
    return {
        expenseCategoryId,
        expenseCategoryName: category?.name?.trim() || '',
    };
}

export function costCategoryFromItem(
    item?: { expenseCategoryId?: string | null; expenseCategoryName?: string | null } | null,
): { expenseCategoryId?: string; expenseCategoryName?: string } {
    const expenseCategoryName = item?.expenseCategoryName?.trim() || '';
    if (!expenseCategoryName) return {};
    const expenseCategoryId = item?.expenseCategoryId?.trim() || '';
    return expenseCategoryId
        ? { expenseCategoryId, expenseCategoryName }
        : { expenseCategoryName };
}

// ----- Service item price snapshots on expenses -----

export function sumPmPartPriceSnapshots(snapshots: PmPartPriceSnapshot[]): number {
    return snapshots.reduce((sum, line) => sum + line.lineTotal, 0);
}

export function parsePmPartsSnapshot(raw: unknown): PmPartPriceSnapshot[] {
    if (!raw) return [];
    try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter((line): line is Record<string, unknown> => Boolean(line) && typeof line === "object")
            .map((line) => ({
                serviceItemId: String(line.serviceItemId ?? ""),
                itemName: String(line.itemName ?? ""),
                quantity: Number(line.quantity) || 0,
                unitPrice: Number(line.unitPrice) || 0,
                lineTotal: Number(line.lineTotal) || 0,
            }))
            .filter((line) => line.serviceItemId || line.itemName);
    } catch {
        return [];
    }
}